import {createContext, use, useCallback} from 'react'

import {
  createDebugEvent,
  isAhpDebugLogEnabled,
  type AhpDebugEvent,
  type AhpDebugEventInput,
} from '../utils/ahp-debug-log'

export const DebugEventsDataContext = createContext<AhpDebugEvent[] | null>(null)
export const DebugEventsAppendContext = createContext<((event: AhpDebugEvent) => void) | null>(null)

export type DebugEventsGetter = () => readonly AhpDebugEvent[]

/**
 * Reads the captured log on demand. Kept separate from
 * `DebugEventsDataContext` so that consumers which only need the log when the
 * user acts do not re-render on every captured transport frame.
 */
export const DebugEventsGetterContext = createContext<DebugEventsGetter | null>(null)

const NO_DEBUG_EVENTS: readonly AhpDebugEvent[] = []
const getNoDebugEvents: DebugEventsGetter = () => NO_DEBUG_EVENTS

/**
 * Returns a stable getter for the captured AHP transport log. Outside a
 * provider it yields an empty list, so surfaces that never capture the log can
 * still offer log-backed actions without crashing.
 */
export function useOptionalDebugEventsGetter(): DebugEventsGetter {
  return use(DebugEventsGetterContext) ?? getNoDebugEvents
}

export type DebugEventRecorder = (event: AhpDebugEventInput) => void

/**
 * Returns a stable callback that adds a user or browser event to the nearest
 * debug log. It does nothing outside a provider or when the debug log is off.
 */
export function useDebugEventRecorder(): DebugEventRecorder {
  const append = use(DebugEventsAppendContext)
  return useCallback(
    (event: AhpDebugEventInput) => {
      if (!append || !isAhpDebugLogEnabled()) return
      append(createDebugEvent(event))
    },
    [append],
  )
}
